/**
 * Decimal number formats
 * Mostly used when a number field has no input mask from fieldModel
 *
 * Decimal decimal part
 *   - Optional: whether decimal part is optional, e.g. 3.00 => 3
 *   - Digits:how many digits after decimal dot by default if scale is not defined
 *
 * Integer integer part
 *   - AutoGroup: group number by comma.
 *
 * Currency decimal part
 *  - Digits:how many digits after decimal dot by default if scale is not defined
 */
interface NumberFormatType {
	readonly Decimal: {
		readonly Optional: boolean;
		readonly Digits: number;
	};
	readonly Integer: {
		readonly AutoGroup: boolean;
	};
	readonly Currency: {
		readonly Digits: number;
	};
}

export const NumberFormat: Readonly<NumberFormatType> = {
	Decimal: {
		Optional: false,
		Digits: 6,
	},
	Integer: {
		// not support leading 0 and optional integer if integer is 0
		AutoGroup: true,
	},
	Currency: {
		Digits: 2,
	},
};

export const MIN_INT: number = -32768;
export const MAX_INT: number = 32767;
export const MIN_LONG: number = -2147483648;
export const MAX_LONG: number = 2147483647;

/**
 * Note: York - WEBUI-1723
 *
 * @see https://en.wikipedia.org/wiki/Double-precision_floating-point_format
 * > If a decimal string with at most 15 significant digits is converted to
 * IEEE 754 double-precision representation, and then converted back to a
 * decimal string with the same number of digits, the final result should match
 * the original string.
 *
 * >  With the 52 bits of the fraction significand appearing in the memory
 * format, the total precision is therefore 53 bits (approximately 16 decimal
 * digits, 53 log10(2) ≈ 15.955)
 *
 * So let's set it to 16 in the frontend.
 *
 * Note: Lucas - WEBUI-4013
 * Update - 16 was too small. We need 17 digits to represent every double precision number. The numbers
 * 2264.4700000000003 and 32774.020000000004 (calculated and saved by the Acurity core as part of a transaction) were
 * were failing WEBUI front end save validation.
 *
 * Not every 17 digit number will have a double precision representation. This was also a for 16 digits.
 */
export const DECIMAL_DIGITS: number = 17;

export const CURRENCY_PREFIX: string = '$ ';
export const PERCENTAGE_SUFFIX: string = ' %';
